import { useState } from 'react';
import { Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem, Alert } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { warehousesApi, type Warehouse, type WarehouseInput } from '../../api/inventory.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function WarehousesPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Warehouse | null>(null);
  const [form, setForm] = useState<WarehouseInput>({ name: '', status: 'active' });
  const [deleting, setDeleting] = useState<Warehouse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['warehouses', page, limit], queryFn: () => warehousesApi.list({ page, limit }) });
  const parents = useQuery({ queryKey: ['warehouses', 'all'], queryFn: () => warehousesApi.list({ limit: 100 }), enabled: open });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['warehouses'] });

  const save = useMutation({
    mutationFn: () => (editing ? warehousesApi.update(editing.id, form) : warehousesApi.create(form)),
    onSuccess: () => { setOpen(false); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const del = useMutation({ mutationFn: (id: number) => warehousesApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setError(extractApiError(e)); setDeleting(null); } });

  const openNew = () => { setEditing(null); setForm({ name: '', status: 'active' }); setError(null); setOpen(true); };
  const openEdit = (w: Warehouse) => { setEditing(w); setForm({ name: w.name, parentId: w.parentId, managerUserId: w.managerUserId, regionId: w.regionId, address: w.address ?? undefined, status: w.status as 'active' | 'inactive' }); setError(null); setOpen(true); };

  const columns: Column<Warehouse>[] = [
    { key: 'code', label: 'Code', width: 120 },
    { key: 'name', label: 'Name' },
    { key: 'parentId', label: 'Parent', render: (r) => (r.parentId ? `#${r.parentId}` : '—') },
    { key: 'managerUserId', label: 'Manager', render: (r) => (r.managerUserId ? `user#${r.managerUserId}` : '—') },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader title="Warehouses" subtitle="Multi-warehouse hierarchy, manager assignment and status." action={hasPermission('warehouse.create') && <Button startIcon={<Add />} variant="contained" onClick={openNew}>New Warehouse</Button>} />

      <DataTable
        columns={columns} rows={list.data?.data ?? []} loading={list.isLoading}
        page={page} limit={limit} total={list.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id}
        emptyTitle="No warehouses"
        actions={(r) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {hasPermission('warehouse.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('warehouse.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </Stack>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit Warehouse' : 'New Warehouse'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField select label="Parent Warehouse" value={form.parentId ?? ''} onChange={(e) => setForm((f) => ({ ...f, parentId: e.target.value === '' ? null : Number(e.target.value) }))} fullWidth>
                <MenuItem value="">None</MenuItem>
                {(parents.data?.data ?? []).filter((w) => w.id !== editing?.id).map((w) => <MenuItem key={w.id} value={w.id}>{w.code} — {w.name}</MenuItem>)}
              </TextField>
              <TextField select label="Status" value={form.status ?? 'active'} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as 'active' | 'inactive' }))} sx={{ width: 160 }}>
                <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Manager User ID" type="number" value={form.managerUserId ?? ''} onChange={(e) => setForm((f) => ({ ...f, managerUserId: e.target.value === '' ? null : Number(e.target.value) }))} fullWidth />
              <TextField label="Region ID" type="number" value={form.regionId ?? ''} onChange={(e) => setForm((f) => ({ ...f, regionId: e.target.value === '' ? null : Number(e.target.value) }))} fullWidth />
            </Stack>
            <TextField label="Address" value={form.address ?? ''} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} fullWidth multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.name || save.isPending} onClick={() => save.mutate()}>Save</Button></DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete warehouse" message={`Delete ${deleting?.name}? Warehouses with stock movements cannot be deleted.`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting.id)} />
    </>
  );
}
